import React, { useCallback } from "react";
import { ANALYSIS_TYPE_OBJECT, ANALYSIS_TYPE_VARIANT } from "../constants";
import { getCellPresentation } from "../utils/cellIntensity";
import ABCXYZInfoTooltip from "./ABCXYZInfoTooltip";

const formatNumber = (value, decimals = 0) => {
  if (value === null || value === undefined) return "—";
  return Number(value).toLocaleString("de-DE", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
};

/**
 * One cell of the 3x3 ABC/XYZ matrix.
 *
 * The stability class (X/Y/Z) picks the color family; the cell's share of
 * items only scales that family color's intensity (see utils/cellIntensity),
 * so the strongest cell within a family visually leads. Text tones adapt via
 * the presentation helper's contrast check. Click is prepared for future
 * dashboard drill-down (the cell's member and object ids ship with the
 * backend response) — for now it only notifies the optional handler / logs.
 */
const ABCXYZMatrixCell = ({
  cell,
  mode,
  intensity = 0,
  onCellClick = null,
  groupByAttribute = null,
  objectType = null,
}) => {
  const empty = cell.item_count === 0;
  const caseNoun = mode === ANALYSIS_TYPE_VARIANT ? "cases" : "objects";

  const isObjectMode = mode === ANALYSIS_TYPE_OBJECT;
  const isVariantMode = mode === ANALYSIS_TYPE_VARIANT;
  let cellNumberLines = null;
  if (isObjectMode && groupByAttribute && objectType) {
    cellNumberLines = [
      {
        label: formatNumber(cell.item_count),
        text: `groups of "${groupByAttribute}" in this class.`,
      },
      {
        label: formatNumber(cell.case_count),
        text: `underlying "${objectType}" objects across those groups.`,
      },
    ];
  } else if (isVariantMode && objectType) {
    cellNumberLines = [
      {
        label: formatNumber(cell.item_count),
        text: `process variants of "${objectType}" in this class.`,
      },
      {
        label: formatNumber(cell.case_count),
        text: `underlying "${objectType}" cases following those variants.`,
      },
    ];
  }

  const handleClick = useCallback(() => {
    // Drill-down stub: ids are already in the response
    // model; future dashboard filtering hooks in here.
    if (onCellClick) onCellClick(cell);
  }, [cell, onCellClick]);

  const presentation = empty
    ? { style: {}, darkText: true }
    : getCellPresentation(cell.xyz_class, intensity);

  // Light-text variants are the fallback for higher intensity bounds.
  const tones = presentation.darkText
    ? {
        count: "text-gray-900",
        percentage: empty ? "text-gray-400" : "text-gray-600",
        label: empty ? "text-gray-400" : "text-gray-600",
        cases: empty ? "text-gray-400" : "text-gray-600",
      }
    : {
        count: "text-white",
        percentage: "text-white/90",
        label: "text-white/90",
        cases: "text-white/75",
      };

  const cellBody = (
    <div className="h-full flex flex-col items-center justify-center text-center gap-1.5 leading-snug cursor-default">
      <div className="flex items-baseline justify-center gap-1.5">
        <span className={`text-xl font-semibold ${tones.count}`}>
          {formatNumber(cell.item_count)}
        </span>
        <span className={`text-sm font-medium ${tones.percentage}`}>
          ({formatNumber(cell.percentage, 1)} %)
        </span>
      </div>
      <div className={`text-sm font-medium ${tones.label}`}>{cell.label}</div>
      {cell.decision ? (
        <div className={`text-xs ${tones.cases}`}>{cell.decision}</div>
      ) : null}
      <div className={`text-xs ${tones.cases}`}>
        {formatNumber(cell.case_count)} {caseNoun} (
        {formatNumber(cell.case_percentage, 1)} %)
      </div>
    </div>
  );

  return (
    <button
      type="button"
      onClick={handleClick}
      style={presentation.style}
      className={`w-full h-full min-h-[114px] rounded-lg border p-2.5 transition-shadow hover:shadow-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${
        empty ? "bg-gray-50 border-gray-200 opacity-60" : ""
      }`}
    >
      {cellNumberLines ? (
        <ABCXYZInfoTooltip
          title="Matrix Cell"
          sections={[]}
          extraLines={cellNumberLines}
          trigger={cellBody}
        />
      ) : (
        cellBody
      )}
    </button>
  );
};

export default ABCXYZMatrixCell;
